<template>
    <a v-if="authStore.getIsLoggedIn" @click="goToDashboard()" :class="['flex w-full justify-between text-nowrap rounded-md border-none px-0 py-2 text-sm xl:max-w-96', dashboardStatus.isActive ? 'cursor-pointer hover:opacity-60' : '']">
        <div class="flex items-center justify-center">
            <div class="">
                <Avatar class="ml-0 overflow-hidden border-none" :image="userStore.loggedInUser.avatar" :label="userStore.loggedInUser.avatar ? '' : userInitials" :size="isMobile ? 'normal' : 'large'" shape="circle" />
            </div>
            <div class="ml-2 max-w-40 md:max-w-96 lg:max-w-52">
                <div :class="['truncate text-lg', userStore.isThemeLight ? 'text-black' : 'text-white']">{{ userStore.loggedInUser.userName }}</div>
                <div v-if="userStore.loggedInUser.appointments > 0 && dashboardStatus.isRequestAppointmentActive" class="mr-4 text-xs font-light text-red-400 xl:mr-6">
                    {{ $t('youHaveUnresolvedRequirements', { n: userStore.loggedInUser.appointments }) }}
                </div>
            </div>
        </div>
        <div class="flex items-center justify-center">
            <div>
                <Badge v-if="userStore.loggedInUser.appointments > 0 && dashboardStatus.isRequestAppointmentActive" :value="userStore.loggedInUser.appointments" class="bg-red-400"></Badge>
            </div>
            <FontAwesomeIcon class="ml-2" v-if="dashboardStatus.isActive" :inverse="!userStore.isThemeLight" icon="fa-sharp fa-solid fa-chevron-right" />
            <div
                v-else
                class="ml-4 cursor-pointer hover:opacity-50"
                key="123"
                v-tooltip="{
                    value: $t('logout'),
                    pt: {
                        root: () => ({
                            class: [
                                // Position
                                'absolute',

                                // Shadow
                                'shadow-none',
                                // Colors
                                userStore.isThemeLight ? 'bg-black text-white' : 'bg-white text-black',
                                'rounded-md',
                            ],
                        }),
                        text: {
                            class: [
                                // Size
                                'text-xs leading-none',

                                // Spacing
                                'p-2',

                                // Shape
                                'rounded-md',

                                // Misc
                                'whitespace-pre-line',
                                'break-words',
                            ],
                        },
                    },
                }"
                @click="logout"
            >
                <FontAwesomeIcon :inverse="!userStore.isThemeLight" size="lg" icon="fa-sharp fa-solid fa-arrow-right-from-bracket" />
            </div>
        </div>
    </a>
    <Button v-else @click="login()" :class="['inline-block w-full text-nowrap bg-white !py-0 pr-6 !text-left text-sm font-light text-gray-500 drop-shadow-[0_2px_15px_rgba(0,0,0,0.10)] transform-gpu hover:drop-shadow-[0_2px_15px_rgba(0,0,0,0.40)]']">
        <img class="h-14 w-36" :src="logo" alt="iKlient" />
        <span>{{ $t('loginClient') }}</span>
    </Button>
</template>

<script setup lang="ts">
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';
import Avatar from 'primevue/avatar';
import Badge from 'primevue/badge';
import { useToast } from 'primevue/usetoast';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { ROUTES } from '@/enums/RoutesEnum';
import { useAuthStore } from '@/stores/auth';
import { useUserStore } from '@/stores/user';

const authStore = useAuthStore();
const router = useRouter();
const userStore = useUserStore();
const { t } = useI18n();
const toast = useToast();

const breakpoints = useBreakpoints(breakpointsTailwind);
const isMobile = breakpoints.smaller('md');

const logo = computed(() => {
    switch (userStore.isThemeLight) {
        case true:
            return '/images/iKlient_logo_client_zone_black.svg';
        case false:
            return '/images/iKlient_logo_client_zone_black.svg'; // Dark mode disabled for now
        default:
            return '/images/iKlient_logo_client_zone_black.svg';
    }
});

const userInitials = computed(() => {
    return userStore.loggedInUser.userName.charAt(0);
});

const login = () => {
    router.push({ name: ROUTES.LOGIN });
};

const goToDashboard = () => {
    if (dashboardStatus.value.isActive) {
        router.push({ name: ROUTES.DASHBOARD });
    }
};

const logout = async () => {
    try {
        authStore.logout();

        toast.add({
            severity: 'success',
            summary: t('logoutSuccess'),
            life: 3000,
        });
    } catch (error) {
        toast.add({
            severity: 'error',
            summary: t('logoutError'),
            life: 3000,
        });
    }
};

const dashboardStatus = computed(() => {
    if (authStore.getIsLoggedIn) {
        const userClinic = userStore.clinics.find((clinic) => clinic.id === userStore.loggedInUser.idLocation);

        return {
            isActive: userClinic?.isBookingDashboard || false,
            isLastAppointmentActive: userClinic?.isBookingDashboardLastAppointment || false,
            isRequestAppointmentActive: userClinic?.isBookingDashboardRequestAppointment || false,
        };
    } else {
        return {
            isActive: false,
            isLastAppointmentActive: false,
            isRequestAppointmentActive: false,
        };
    }
});
</script>
